import { stableStringify } from '@fullstory/util/hash';
import { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type AppCacheResult<TData> = {
  data: TData | undefined;
  dataUpdatedAt: number | undefined;
  onForceRefresh: () => void;
  onCacheSwap: () => void;
  status: AppCacheStatus;
};

export enum AppCacheStatus {
  // The default status before any loading has happened
  Initial = 'INITIAL',
  // Fetching new data on initial load or in refresh state
  IsLoading = 'IS_LOADING',
  // Errored after fetching for data
  LoadFailed = 'LOAD_FAILED',
  // Fetching new data after being in middle state
  IsRefetching = 'IS_REFETCHING',
  // Holding onto newer data
  HasData = 'HAS_DATA',
  // Rendering new data
  Fresh = 'FRESH',
}

export type QueryResultLike<TQueryFnData, TData = TQueryFnData, TError = Error> = Pick<
  UseQueryResult<TData, TError>,
  | 'data'
  | 'dataUpdatedAt'
  | 'isError'
  | 'isSuccess'
  | 'isLoading'
  | 'isRefetching'
  | 'isRefetchError'
  | 'isFetching'
> & {
  refetch(): void;
};

/**
 * A hook that provides custom caching and mediates access to the latest query data.
 * It returns data about the last update as well as callbacks used to take the latest changes or force a refresh.
 * @param queryKey The query key for the request being cached
 * @param queryResult They query result from useQuery for the request being cached
 * @returns An object containing the cached data, the last time the data was updated, and methods to force refresh the data and swap the cache.
 * Provides props to be used with the {@link DetailViewVisualizationHeader} component.
 */
export function useCachedData<TQueryFnData, TData = TQueryFnData, TError = Error>(
  queryKey: QueryKey,
  queryResult: QueryResultLike<TQueryFnData, TData, TError>,
): AppCacheResult<TData> {
  const { refetch, dataUpdatedAt, data, isError, isSuccess, isLoading, isRefetching, isFetching } =
    queryResult;

  const [{ cacheKey, cacheData, cacheUpdatedAt, cacheStatus }, setState] = useState<{
    cacheKey: QueryKey | undefined;
    cacheData: TData | undefined;
    cacheUpdatedAt: number | undefined;
    cacheStatus: AppCacheStatus;
  }>({
    cacheKey: queryKey,
    cacheData: undefined,
    cacheUpdatedAt: undefined,
    cacheStatus: AppCacheStatus.Initial,
  });

  useEffect(() => {
    const isSameKey = stableStringify(cacheKey ?? '') === stableStringify(queryKey);

    if (isSameKey) {
      // Initial -> IsLoading - this happens when the hook is first mounted and the query is loading
      if (cacheStatus === AppCacheStatus.Initial && isLoading && !cacheData) {
        setState({
          cacheKey,
          cacheData: undefined,
          cacheUpdatedAt: undefined,
          cacheStatus: AppCacheStatus.IsLoading,
        });
        return;
      }

      // IsLoading -> Fresh | LoadFailed - this happens when the query finishes loading initially
      if (cacheStatus === AppCacheStatus.IsLoading) {
        if (isSuccess && data) {
          setState({
            cacheKey,
            cacheData: data,
            cacheUpdatedAt: dataUpdatedAt,
            cacheStatus: AppCacheStatus.Fresh,
          });
        } else if (isError) {
          setState({
            cacheKey,
            cacheData,
            cacheUpdatedAt,
            cacheStatus: AppCacheStatus.LoadFailed,
          });
        }
        return;
      }

      // Fresh | HasData | LoadFailed -> IsRefetching
      if (
        [AppCacheStatus.Fresh, AppCacheStatus.HasData, AppCacheStatus.LoadFailed].includes(
          cacheStatus,
        ) &&
        isRefetching
      ) {
        setState({
          cacheKey,
          cacheData,
          cacheUpdatedAt,
          cacheStatus: AppCacheStatus.IsRefetching,
        });
        return;
      }

      // IsRefetching -> Fresh | LoadFailed
      if (cacheStatus === AppCacheStatus.IsRefetching && !isRefetching) {
        if (isSuccess && data) {
          setState({
            cacheKey,
            cacheData: data,
            cacheUpdatedAt: dataUpdatedAt,
            cacheStatus: AppCacheStatus.Fresh,
          });
        } else if (isError) {
          setState({
            cacheKey,
            cacheData,
            cacheUpdatedAt,
            cacheStatus: AppCacheStatus.LoadFailed,
          });
        }
        return;
      }

      // Fresh -> HasData
      const hasFreshData = dataUpdatedAt > (cacheUpdatedAt ?? 0);
      if (
        cacheStatus === AppCacheStatus.Fresh &&
        isSuccess &&
        hasFreshData &&
        !isLoading &&
        !isRefetching &&
        !isFetching
      ) {
        setState({
          cacheKey,
          cacheData,
          cacheUpdatedAt,
          cacheStatus: AppCacheStatus.HasData,
        });
        return;
      }

      return;
    }

    // Query Change -> loading
    if (isLoading) {
      setState({
        cacheData: undefined,
        cacheKey: undefined,
        cacheUpdatedAt: undefined,
        cacheStatus: AppCacheStatus.IsLoading,
      });
      return;
    }

    // Query Change -> success
    if (isSuccess && data) {
      setState({
        cacheData: data,
        cacheKey: queryKey,
        cacheUpdatedAt: dataUpdatedAt,
        cacheStatus: AppCacheStatus.Fresh,
      });
      return;
    }

    // Query Change -> LoadFailed
    if (isError) {
      setState({
        cacheData: data,
        cacheKey: queryKey,
        cacheUpdatedAt: dataUpdatedAt,
        cacheStatus: AppCacheStatus.LoadFailed,
      });
    }
  }, [
    cacheKey,
    cacheData,
    cacheUpdatedAt,
    cacheStatus,
    data,
    dataUpdatedAt,
    queryKey,
    isError,
    isFetching,
    isLoading,
    isRefetching,
    isSuccess,
  ]);

  // On cache swap, we need to update to fresh and update data
  const onCacheSwap = useCallback(() => {
    if (!data || !dataUpdatedAt || cacheStatus !== AppCacheStatus.HasData) {
      return;
    }

    setState({
      cacheKey,
      cacheData: data,
      cacheUpdatedAt: dataUpdatedAt,
      cacheStatus: AppCacheStatus.Fresh,
    });
  }, [cacheKey, cacheStatus, data, dataUpdatedAt]);

  // On force refresh we need to trigger a refetch and update the status
  const onForceRefresh = useCallback(() => {
    setState({
      cacheKey,
      cacheData,
      cacheUpdatedAt,
      cacheStatus: AppCacheStatus.IsRefetching,
    });
    refetch();
  }, [cacheKey, cacheData, cacheUpdatedAt, refetch]);

  return useMemo(
    () => ({
      data: cacheData,
      dataUpdatedAt: cacheUpdatedAt,
      onForceRefresh,
      onCacheSwap,
      status: cacheStatus,
    }),
    [cacheData, cacheUpdatedAt, cacheStatus, onCacheSwap, onForceRefresh],
  );
}
